import { Toggle } from '@insurancemenu/imenu-components-lib';
import NoDataPlaceholder from 'components/common/general/NoDataPlaceholder';
import PageFooter from 'components/common/general/layouts/PageFooter';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import {
  RFQ_PROPOSAL_CARD_DATA,
  getRFQProposalChecklistURL,
} from 'global/checklists-helpers/RFQProposalChecklistHelper';
import useRfqQuoteUrlParams from 'global/hooks/useRfqQuoteUrlParams';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePickCarriersStore } from 'store/pages/rfq/initiate-rfq/pick-carriers/pageStore';
import { observer } from 'mobx-react-lite';
import { ListStyled, WrapperStyled, ToggleWrapperStyled } from './styles';
import ProductLineCard from './ProductLineCard';
import { PICK_CARRIERS_NO_DATA_PLACEHOLDER_TEST_ID, PICK_CARRIERS_TOGGLE_TEST_ID } from '../constants';

const PickCarriersContent: React.FC = observer(() => {
  const [areProductLinesCollapsed, setAreProductLinesCollapsed] = useState(false);

  const history = useHistory();
  const { periodId, cartId } = useRfqQuoteUrlParams();
  const { isLoading, carriersPerProductLine, isSubmitButtonDisabled, groupId, getInitialData, submitStep } =
    usePickCarriersStore();

  const isDataMissing = carriersPerProductLine.length === 0;

  useEffect(() => {
    void getInitialData(cartId);
  }, []);

  const onDefaultRedirect = (): void => {
    const checklistURL = getRFQProposalChecklistURL({ periodId, cartId });
    history.push(checklistURL);
  };

  const onToggleChange = (): void => {
    setAreProductLinesCollapsed((prev) => !prev);
  };

  const onStepComplete = async (): Promise<void> => {
    // After completing this step, a new response checklist will be displayed on the RFQ checklist page.
    // The backend replace the Initiate RFQ checklist to the Response RFQ checklist
    // After this action, the user will no longer have access to the Initiate RFQ checklist.
    await submitStep(periodId, cartId);
    history.push(`/group/${groupId}`);
  };

  return (
    <>
      <RFQChecklistOptionHeader
        title={RFQ_PROPOSAL_CARD_DATA.PICK_CARRIERS.title}
        subtitle={RFQ_PROPOSAL_CARD_DATA.PICK_CARRIERS.text}
        onRedirectBack={onDefaultRedirect}
      />
      <ToggleWrapperStyled>
        {!isLoading && !isDataMissing && (
          <Toggle
            name="collapsePLines"
            labelActive="Collapse Product Lines"
            labelInactive="Collapse Product Lines"
            fixedLabelSide="right"
            checked={areProductLinesCollapsed}
            onChange={onToggleChange}
            data-test-id={PICK_CARRIERS_TOGGLE_TEST_ID}
          />
        )}
      </ToggleWrapperStyled>
      <WrapperStyled loading={isLoading} minHeight="24rem">
        {isDataMissing ? (
          <NoDataPlaceholder text="No carriers available" data-test-id={PICK_CARRIERS_NO_DATA_PLACEHOLDER_TEST_ID} />
        ) : (
          <ListStyled>
            {carriersPerProductLine.map(({ productLine }) => (
              <ProductLineCard
                key={productLine.productLineId}
                productLineId={productLine.productLineId}
                status={productLine.status}
                carriers={productLine.carriers}
                isSectionCollapsed={areProductLinesCollapsed}
              />
            ))}
          </ListStyled>
        )}
      </WrapperStyled>
      <PageFooter
        disableCompleteButton={isLoading || isSubmitButtonDisabled}
        onComplete={onStepComplete}
        onBack={onDefaultRedirect}
      />
    </>
  );
});

export default PickCarriersContent;
